import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { LocaleContext } from './layout';

interface TranslationNode {
  name: string;
  translations: {
    [key: string]: string;
  };
}

interface TranslationQuery {
  rawData: {
    edges: {
      node: TranslationNode;
    }[];
  };
}

const useTranslations = (locale: string): { [key: string]: string } => {
  // Query the JSON files in <rootDir>/i18n/translations
  const { rawData }: TranslationQuery = useStaticQuery(query);

  // Simplify the response from GraphQL
  const simplified = rawData.edges.map(item => {
    return {
      name: item.node.name,
      translations: item.node.translations
    };
  });

  // Only return translations for the current locale
  const { translations } = simplified.filter(lang => lang.name === locale.split('-')[0])[0];

  return translations;
};

export const useTranslationsContext = (): { [key: string]: string } => {
  const { locale } = React.useContext(LocaleContext);
  // Query the JSON files in <rootDir>/i18n/translations
  const { rawData }: TranslationQuery = useStaticQuery(query);

  // Simplify the response from GraphQL
  const simplified = rawData.edges.map(item => {
    return {
      name: item.node.name,
      translations: item.node.translations
    };
  });

  // Only return translations for the current locale
  const { translations } = simplified.filter(lang => lang.name === locale.split('-')[0])[0];

  return translations;
};

export default useTranslations;

const query = graphql`
  query useTranslations {
    rawData: allFile(filter: { sourceInstanceName: { eq: "translations" } }) {
      edges {
        node {
          name
          translations: childTranslationsJson {
            navigation_about_us
            navigation_contact
            navigation_cars
            navigation_services
            navigation_news
            skip_to_main_content
            skip_to_navigation
            skip_to_footer
            start_virtual_tour
            learn_more
            show_all_cars
            show_all_news
            write_us
            first_registration
            mileage
            fuel
            gearbox
            power
            show_details
            inner_fuel_consumption
            outer_fuel_consumption
            combined_fuel_consumption
            co2_emission
            energy_efficiency_class
            description
            technical_data
            anfrage_senden
            make
            modell
            category
            exterior_color
            general_inspection
            emission_sticker
            brutto
            netto
            vat
            show_all_images
            cars_title
            sort_by
            price_asc
            price_desc
            first_registration_asc
            first_registration_desc
            mileage_asc
            mileage_desc
            full_text_search
            price
            any
            home
            count
            next
            previous
            financing_calculation
            trade_in
            features
            consumption
            car_id
            number_of_previous_owners
            cubic_capacity
            no_data
            filterTitle
            close
            prefix
            mr
            mrs
            select_option
            first_name
            last_name
            email
            phone
            own_car_model
            own_car_first_registration
            own_car_mileage
            own_car_accident_free
            own_car_damages
            own_car_vin
            own_car_offered_price
            own_car_images
            registration_document
            notes
            privacy_policy
            own_car_damages_description
            notes_description
            submit
            first
            second
            third
            fourth
            personal_data
            vehicle_data
            vehicle_images
            summary
            submit_successfully
            thank_you
            fill_out_all_required_fields
            error_required
            error_invalid_phone
            error_invalid_email
            error_invalid_vin
            error_at_least_one_image
            error_combined_max_twenty_mb
            error_only_images
            error_max_ten_images
            error_only_five_mb
            error_only_pdf_or_images
            error_privacy_policy
            error_images_too_large
            error_image_file_too_large
            error_registration_document_too_large
            error_image_processing_failed
            error_registration_document_processing_failed
            error_only_supported_images
            error_only_pdf_or_supported_images
            about_us
            our_showroom
            our_team
            team_members_load_error
            end_tour
            first_class
            first_class_description
            see_for_yourself
            see_for_yourself_description
            to_our_cars
            to_our_services
            car_request
            message
            message_description
            additional_services
            additional_services_description
            dekra_seal
            dekra_seal_description
            admission
            admission_description
            short_term_registration
            short_term_registration_description
            export_registration
            export_registration_description
            transport
            transport_description
            compare_packages
            request_car_purchasing
            and_many_more
            services_around_car_sale
            financing_service
            trade_in_service
            dekra_service
            export_service
            registration_service
            short_term_registration_service
            transport_service
            car_preparation_service
            our_prices
            select_car_size
            small_car
            standard_car
            big_car
            small_car_description
            standard_car_description
            big_car_description
            on_request
            from
            FAQ
            FAQ_description
            FAQ_question_1
            FAQ_answer_1
            FAQ_question_2
            FAQ_answer_2
            FAQ_question_3
            FAQ_answer_3
            package_name
            trade_in_instead
            trade_in_to_cars_link
            visit_us
            contact_us
            follow_us
            opening_hours
            monday_to_friday
            saturday
            sunday_and_holidays
            only_showroom
            telefax
            relevant_sort
            change_language
            company
            social_media
            imprint
            accessibility
            form_privacy_policy
            car_purchasing
            nav_group_purchase_financing
            nav_group_vehicle_service
            nav_group_logistics
            request_call_back
            go_to_instagram
            select_language
            confirm
            information
            financing_inquire
            financing_type
            standingData
            birthPlace
            nationality
            guarantorFor
            residence_data
            street
            zipCode
            city
            country
            livingType
            livingLongerThanTwoYears
            previousAddress
            job
            occupationalGroup
            branch
            branchDetail
            jobTitle
            employer
            limited
            limitedUntil
            netIncome
            payroll
            otherIncome
            rent
            additionalCosts
            businessForm
            companyName
            companyAddress
            companyZipCode
            companyCity
            companyCountry
            companyAddressSinceTwoYears
            previousCompanyAddress
            formOfFoundation
            foundationDate
            numberOfEmployees
            annualSales
            isCraftsman
            bankData
            bankName
            iban
            bic
            desiredRate
            desiredDownPayment
            desiredPaymentDate
            single
            married
            divorced
            separated
            widowed
            german
            french
            turkish
            portuguese
            british
            irish
            norwegian
            swedish
            russian
            dutch
            austrian
            swiss
            polish
            greek
            italian
            other
            ownership
            sublease
            goodsProductionOrProcessing
            constructionIndustry
            miningAndExtractionOfStones
            itResearchAndServices
            energyAndWaterSupply
            educationTeachingHealthAndSocialServices
            hospitalityIndustry
            realEstateAndHousing
            tradeMaintenanceAndRepairOfGoods
            creditAgricultureFishing
            publicBudgetsSocialSecurity
            publicAndPersonalServices
            nonProfitOrganizations
            transportAndCommunication
            rentalAndLeasingOfMovableProperty
            austria
            belgium
            croatia
            cuba
            cyprus
            czechRepublic
            denmark
            djibouti
            dominica
            dominicanRepublic
            eastTimor
            ecuador
            egypt
            elSalvador
            equatorialGuinea
            eritrea
            estonia
            ethiopia
            falklandIslands
            faroeIslands
            fiji
            finland
            france
            metropolitanFrance
            frenchGuiana
            frenchPolynesia
            frenchSouthernAndAntarcticLands
            gabon
            gambia
            georgia
            germany
            ghana
            gibraltar
            greece
            greenland
            grenada
            guadeloupe
            guam
            guatemala
            guinea
            guineaBissau
            guyana
            haiti
            heardAndMcDonaldIslands
            vaticanCity
            honduras
            hongKong
            hungary
            iceland
            india
            indonesia
            iran
            iraq
            ireland
            israel
            italy
            jamaica
            japan
            jordan
            kazakhstan
            kenya
            kiribati
            northKorea
            southKorea
            kuwait
            kyrgyzstan
            laos
            latvia
            lebanon
            lesotho
            liberia
            libya
            liechtenstein
            lithuania
            luxembourg
            macau
            macedonia
            madagascar
            malawi
            malaysia
            maldives
            mali
            malta
            marshallIslands
            martinique
            mauritania
            mauritius
            mayotte
            mexico
            micronesiaFederatedStatesOf
            moldova
            monaco
            mongolia
            montserrat
            morocco
            mozambique
            myanmar
            namibia
            nauru
            nepal
            netherlands
            netherlandsAntilles
            newCaledonia
            newZealand
            nicaragua
            niger
            nigeria
            niue
            norfolkIsland
            northernMarianaIslands
            norway
            oman
            pakistan
            palau
            panama
            papuaNewGuinea
            paraguay
            peru
            philippines
            pitcairn
            poland
            portugal
            puertoRico
            qatar
            reunion
            romania
            russianFederation
            rwanda
            stKittsAndNevis
            stLucia
            stVincentAndTheGrenadines
            samoa
            sanMarino
            saoTomeAndPrincipe
            saudiArabia
            senegal
            seychelles
            sierraLeone
            singapore
            slovakia
            slovenia
            solomonIslands
            somalia
            southAfrica
            southGeorgiaAndTheSouthSandwichIslands
            spain
            sriLanka
            stHelena
            stPierreAndMiquelon
            sudan
            suriname
            svalbardAndJanMayenIslands
            swaziland
            sweden
            switzerland
            syria
            taiwan
            tajikistan
            tanzania
            thailand
            togo
            tokelau
            tonga
            trinidadAndTobago
            tunisia
            turkey
            turkmenistan
            turksAndCaicosIslands
            tuvalu
            uganda
            ukraine
            unitedArabEmirates
            unitedKingdom
            unitedStates
            unitedStatesMinorOutlyingIslands
            uruguay
            uzbekistan
            vanuatu
            venezuela
            vietnam
            britishVirginIslands
            usVirginIslands
            wallisAndFutunaIslands
            westernSahara
            yemen
            yugoslavia
            zambia
            zimbabwe
            individualEnterprise
            civilPartnership
            registeredMerchant
            generalPartnership
            limitedPartnership
            limitedLiabilityCompany
            jointStockCompany
            gmbhAndCoKG
            rental
            dependentAgent
            academicProfessionDependent
            employee
            workerIncludingLaborer
            unemployed
            asylumSeeker
            apprentice
            civilServantLowerMiddleService
            diplomat
            skilledWorker
            cateringIndustryEmployee
            housewifeOrHousehusband
            agricultureDependent
            driver
            healthcareEmployeeIncludingDoctors
            seniorEmployeeManager
            officerProfessionalSoldier
            peopleWithoutVocationalTrainingWelfareRecipients
            retireePensioner
            judgeCivilServantHigherService
            seasonalWorker
            student
            selfEmployedFreelancerFarmer
            militaryCivilianServiceProvider
            careerSoldier
            privateFinancing
            commercialFinancing
            guarantorFinancing
            yes
            no
            upload
            upload_images
            images_upload_hint
            images_upload_limit_reached
            images_selected_singular
            images_selected_plural
            upload_document
            compressing_images
            compressing_document
            upload_processing_hint
            upload_submit_processing_hint
            upload_selected_file_aria
            upload_remove_file_aria
            identification
            payrollInfo
            maritalStatus
            fifteenOfMonth
            firstOfMonth
            nonBinding
            ssl
            noAgencyFees
            financingPeriod
            isBallonFinancing
            financingCalculationDisclaimer
            financingCanVary
            acquisitionPrice
            effeciveInterestRate
            monthlyRate
            totalAmount
            downPayment
            finalInstallment
            requestNonBinding
            months
            huNew
            financing_service_description
            which_car_financing
            no_cars_found
            car_purchase_step_one
            car_purchase_step_two
            car_purchase_step_three
            car_purchase_step_one_description
            car_purchase_step_two_description
            car_purchase_step_three_description
            car_preparation_service_description
            news_description
            contact_description
            cars_description
            services_description
            car_service_description
            car_purchase_description
            cookie_consent_modal_title
            cookie_consent_modal_description
            accept_all
            reject_all
            cookie_settings_title
            save_settings
            name
            domain
            expiration
            cookie_usage
            necessary_cookies
            necessary_cookies_description
            statistics_cookies
            statistics_cookies_description
            own_car_previous_owners
            own_car_reimport
            own_car_reimported_from
            contact
            noContact
            error
            livingSince
            combined_power_consumption
            wltp_combined_fuel_consumption
            wltp_combined_power_consumption
            wltp_combined_co2_emission
            wltp_electric_range
            wltp_weighted_combined_fuel_consumption
            wltp_weighted_combined_co2_emission
            wltp_weighted_combined_power_consumption
            combined
            envkvDisclaimer
            underlyingFuelType
            not_available
            weightedCombinedFuel
            weightedCombinedPower
            combinedFuelConsumption
            cityFuelConsumption
            ruralFuelConsumption
            suburbanFuelConsumption
            highwayFuelConsumption
            combinedPowerConsumption
            cityPowerConsumption
            ruralPowerConsumption
            suburbanPowerConsumption
            highwayPowerConsumption
            fuelPrice
            powerPrice
            consumptionCosts
            consumptionTax
            annualAverage
            co2Costs
            range
            co2EmissionCombined
            co2ClassCombined
            co2EmissionDischarged
            co2ClassDischarged
            co2CostsLowDescription
            co2CostsMiddleDescription
            co2CostsHighDescription
            fuelConsumption
            powerConsumption
            co2Class
            co2EmissionDisclaimer
            wantTradeIn
            wantTradeInNo
            wantTradeInYes
            whichCarTradeIn
            wltpEquivalentAllElectricRange
            drive
            combustionEngine
            electricMotor
            pluginHybrid
            otherEnergySource
            electricity
            tireShop
            tireShopDescription
            tireShopLink
            alt_logo_single
            alt_logo_text
            alt_logo_full
            featured_vehicles
            alt_google_reviews
            alt_mobile_reviews
            alt_santander_logo
            alt_car_image
            alt_car_image_of
            alt_news_image
            alt_news_image_for
            alt_team_member
            alt_team_member_photo
            alt_service_image
            alt_service_image_for
            alt_energy_efficiency_label
            alt_instagram_image
            alt_tire_shop_image
            alt_virtual_tour
            alt_about_us_image
            alt_audi_logo
            alt_bmw_logo
            alt_mercedes_logo
            alt_volkswagen_logo
            alt_tesla_logo
            alt_more_brands
            iframe_virtual_tour_title
            link_mobile_reviews
            link_google_reviews
            link_instagram
            link_tiktok
            link_facebook
            link_youtube
            aria_preview_image
            autoankauf_title
            autoankauf_description
            autoankauf_hero_title
            autoankauf_hero_subtitle
            autoankauf_intro_title
            autoankauf_intro_text
            autoankauf_expertise_title
            autoankauf_expertise_text
            autoankauf_passion_title
            autoankauf_passion_text
            autoankauf_network_title
            autoankauf_network_text
            autoankauf_sell_title
            autoankauf_sell_text
            autoankauf_cta_title
            autoankauf_process_title
            autoankauf_why_us_title
            autoankauf_why_expertise
            autoankauf_why_expertise_text
            autoankauf_why_fair
            autoankauf_why_fair_text
            autoankauf_why_fast
            autoankauf_why_fast_text
            autoankauf_why_trust
            autoankauf_why_trust_text
            autoankauf_brands_title
            autoankauf_brands_text
            car_purchase_step_one
            car_purchase_step_two
            car_purchase_step_three
            car_purchase_step_one_description
            car_purchase_step_two_description
            car_purchase_step_three_description
            request_car_purchasing
            alt_autoankauf_hero
            alt_autoankauf_showroom
            alt_autoankauf_exterior
            autoankauf_audi_rs_hero_title
            autoankauf_audi_rs_title
            autoankauf_bmw_m_hero_title
            autoankauf_bmw_m_title
            autoankauf_mustang_hero_title
            autoankauf_mustang_title
            alt_autoankauf_rs_hero
            alt_autoankauf_bmw_m_hero
            share
            share_article
            form_steps
            old_financing_redemption
            autoankauf_why_financing_title
            autoankauf_why_financing_text
            navigation_jobs
            jobs_heading
            jobs_description
            jobs_cta
            frontscheibe_title
            frontscheibe_description
            frontscheibe_hero_title
            frontscheibe_hero_subtitle
            frontscheibe_hero_cta
            frontscheibe_intro_title
            frontscheibe_intro_text
            frontscheibe_services_title
            frontscheibe_service_1_title
            frontscheibe_service_1_text
            frontscheibe_service_2_title
            frontscheibe_service_2_text
            frontscheibe_service_3_title
            frontscheibe_service_3_text
            frontscheibe_service_4_title
            frontscheibe_service_4_text
            frontscheibe_service_5_title
            frontscheibe_service_5_text
            frontscheibe_service_6_title
            frontscheibe_service_6_text
            frontscheibe_why_title
            frontscheibe_why_text
            frontscheibe_brands_title
            frontscheibe_brands_list
            frontscheibe_costs_title
            frontscheibe_costs_text
            frontscheibe_process_title
            frontscheibe_step_one_title
            frontscheibe_step_one_description
            frontscheibe_step_two_title
            frontscheibe_step_two_description
            frontscheibe_step_three_title
            frontscheibe_step_three_description
            frontscheibe_cta_title
            frontscheibe_cta_text
            frontscheibe_cta_button
            alt_frontscheibe_hero
            alt_frontscheibe_expertise
            frontscheibe_service_teaser_title
            frontscheibe_service_teaser_description
            frontscheibe_form_title
            frontscheibe_form_car_make_label
            frontscheibe_form_car_model_label
            frontscheibe_request_subject
            newsletter_eyebrow
            newsletter_title
            newsletter_open_signup
            newsletter_detail_title
            newsletter_description
            newsletter_email_label
            newsletter_email_placeholder
            newsletter_consent_before
            newsletter_consent_link
            newsletter_consent_after
            newsletter_submit
            newsletter_success
            newsletter_error_invalid_email
            newsletter_error_consent
            newsletter_error_verification
            newsletter_error_generic
            newsletter_unavailable
            newsletter_confirmation_title
            newsletter_confirmation_text
            newsletter_confirmation_cta
            newsletter_landing_eyebrow
            newsletter_landing_title
            newsletter_landing_subtitle
            newsletter_landing_form_title
            newsletter_landing_seo_title
            newsletter_landing_seo_description
            whatsapp_contact
          }
        }
      }
    }
  }
`;
